import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Field, Form as FinalForm } from "react-final-form";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import Section from "../../common/section";
import TooltipButton, { TooltipTarget } from "../../common/tooltipButton";
import { TextAreaInput, TextInput } from "../../common/fields";
import Label from "../../common/fields/Label";
import * as Validations from "../../common/form/validator";
import {
  MATCH_BY_EMAIL,
  MATCH_BY_USERNAME,
  PROTOCOL_OIDC,
  PROTOCOL_SAML,
  daysUntil,
  redirectTo,
  ssoPayload,
} from "../../../helpers/SsoHelper";
import { errorMessageId } from "../../../helpers/ErrorMessage";
import * as SsoService from "../../../services/SsoService";
import { useSsoSettings } from "./useSsoSettings";
import "./company-sign-in-tab.scss";

const PROTOCOLS = [
  { value: PROTOCOL_OIDC, label: "sso_protocol_oidc", shortLabel: "sso_protocol_oidc_short", recommended: true },
  { value: PROTOCOL_SAML, label: "sso_protocol_saml" },
];

const MATCHES = [
  { value: MATCH_BY_EMAIL, label: "sso_match_email" },
  { value: MATCH_BY_USERNAME, label: "sso_match_username" },
];

// Days before expiry from which a certificate is flagged.
const EXPIRY_WARNING_DAYS = 30;

// API errors about fields inside each disclosure: the disclosure opens to show the field.
const ADVANCED_ERRORS = ["sso_invalid_match_by", "sso_username_claim_required"];
const MANUAL_ERRORS = [
  "sso_entity_id_required",
  "sso_sign_on_url_invalid",
  "sso_certificate_required",
  "sso_certificate_invalid",
  "sso_certificate_expired",
  "sso_too_many_certificates",
];

const required = Validations.composeValidators(Validations.required);

// One of a few choices, as radio buttons with their own labels.
const RadioGroupInput = ({ input, meta, label, options, disabled, errorId }) => {
  const intl = useIntl();
  return (
    <div className="edexlms-field" data-testid={`field-${input.name}`}>
      <Label label={label} disabled={disabled} required />
      <div className="flex flex-wrap gap-4 mt-2">
        {options.map((option) => {
          const id = `${input.name}-${option.value}`;
          return (
            <div key={option.value} className="sso-radio flex align-items-center gap-2">
              <RadioButton
                inputId={id}
                name={input.name}
                value={option.value}
                checked={Number(input.value) === option.value}
                onChange={() => input.onChange(option.value)}
                disabled={disabled}
              />
              <label htmlFor={id}>{intl.formatMessage({ id: option.label })}</label>
            </div>
          );
        })}
      </div>
      {(meta?.submitFailed && meta?.error && errorId && (
        <div className="edexlms-field-error">{intl.formatMessage({ id: errorId })}</div>
      )) ||
        null}
    </div>
  );
};

// The protocol as a segmented choice; one option is always selected.
const ProtocolInput = ({ input, meta, disabled, errorId }) => {
  const intl = useIntl();
  const options = PROTOCOLS.map((option) => ({
    ...option,
    label: intl.formatMessage({ id: option.label }),
    shortLabel: option.shortLabel ? intl.formatMessage({ id: option.shortLabel }) : null,
  }));
  return (
    <div className="edexlms-field" data-testid={`field-${input.name}`}>
      <SelectButton
        className="sso-protocol"
        value={Number(input.value) || null}
        options={options}
        allowEmpty={false}
        disabled={disabled}
        aria-label={intl.formatMessage({ id: "sso_protocol" })}
        onChange={(event) => event.value && input.onChange(event.value)}
        itemTemplate={(option) => (
          <span className="flex flex-wrap align-items-center justify-content-center column-gap-2">
            {/* On phones a long name gives way to its short form */}
            <span className={option.shortLabel ? "sso-protocol-full" : undefined}>{option.label}</span>
            {(option.shortLabel && (
              <span className="sso-protocol-short" aria-hidden="true">
                {option.shortLabel}
              </span>
            )) ||
              null}
            {(option.recommended && (
              <span className="sso-recommended">{intl.formatMessage({ id: "sso_recommended" })}</span>
            )) ||
              null}
          </span>
        )}
      />
      {(meta?.submitFailed && meta?.error && errorId && (
        <div className="edexlms-field-error">{intl.formatMessage({ id: errorId })}</div>
      )) ||
        null}
    </div>
  );
};

// A setting as a row: its label and reason on the left, the switch on the right.
const SwitchRow = ({ inputId, label, help, checked, onChange, disabled, tooltip, testId }) => (
  <div className="sso-switch-row" data-testid={testId}>
    <div className="sso-switch-row-text">
      <label htmlFor={inputId} className={`sso-switch-row-label ${disabled ? "disabled" : ""}`}>
        {label}
      </label>
      {(help && (
        <div id={`${inputId}-help`} className="sso-switch-row-help">
          {help}
        </div>
      )) ||
        null}
    </div>
    <TooltipTarget tooltip={tooltip} tooltipOptions={{ position: "bottom" }}>
      <InputSwitch
        inputId={inputId}
        checked={Boolean(checked)}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={help ? `${inputId}-help` : undefined}
      />
    </TooltipTarget>
  </div>
);

// A form switch shown as a SwitchRow.
const SwitchRowInput = ({ input, label, help, disabled }) => (
  <SwitchRow
    inputId={input.name}
    testId={`field-${input.name}`}
    label={label}
    help={help}
    checked={input.checked}
    onChange={() => input.onChange(!input.checked)}
    disabled={disabled}
  />
);

// A link-style button that shows or hides the part below it. The part stays
// mounted while hidden, so its fields keep their values and validation.
const Disclosure = ({ id, label, defaultOpen = false, forceOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen || forceOpen);
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);
  return (
    <div className="sso-disclosure">
      <Button
        type="button"
        link
        className="sso-disclosure-toggle"
        icon={`pi ${open ? "pi-chevron-down" : "pi-chevron-right"}`}
        label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      />
      <div id={id} className="sso-disclosure-body" hidden={!open}>
        <div className="flex flex-column gap-3">{children}</div>
      </div>
    </div>
  );
};

// A saved value shown to someone who cannot change it: as text that wraps,
// not a cut-off input.
const ReadOnlyText = ({ input, label }) => (
  <div className="edexlms-field" data-testid={`field-${input.name}`}>
    <Label label={label} disabled />
    <div className="sso-read-only-value">{input.value || "NA"}</div>
  </div>
);

// Copies text to the clipboard; where that is not allowed, selects it so it
// can be copied by hand.
const copyText = (value, element, message) => {
  const selectIt = () => {
    const selection = window.getSelection?.();
    if (selection && element) {
      const range = document.createRange();
      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    toast.info(message("sso_copy_selected"));
  };
  try {
    if (!navigator.clipboard?.writeText) {
      selectIt();
      return;
    }
    navigator.clipboard.writeText(value).then(() => toast.success(message("sso_copied")), selectIt);
  } catch (error) {
    selectIt();
  }
};

// A value the organisation's IT team copies into its sign-in service.
const ItValue = ({ label, value, testId, note }) => {
  const intl = useIntl();
  const valueRef = useRef(null);
  const message = (id, values) => intl.formatMessage({ id }, values);
  return (
    <div className="sso-it-value flex flex-column gap-1">
      <span className="sso-it-value-label">{label}</span>
      <div className="sso-it-value-box">
        <code ref={valueRef} data-testid={testId}>
          {value}
        </code>
        <Button
          type="button"
          outlined
          severity="secondary"
          className="sso-copy-button"
          label={message("sso_copy")}
          aria-label={message("sso_copy_label", { label })}
          onClick={() => copyText(value, valueRef.current, message)}
        />
      </div>
      {note || null}
    </div>
  );
};

const initialValuesOf = (connection) => ({
  // A new connection starts on OIDC
  protocol: connection?.protocol ?? PROTOCOL_OIDC,
  isEnabled: connection?.isEnabled ?? false,
  allowPasswordSignIn: connection?.allowPasswordSignIn ?? true,
  matchBy: connection?.matchBy ?? MATCH_BY_EMAIL,
  usernameClaim: connection?.usernameClaim || "",
  samlMetadataUrl: connection?.samlMetadataUrl || "",
  samlIdpEntityId: connection?.samlIdpEntityId || "",
  samlSignOnUrl: connection?.samlSignOnUrl || "",
  samlCertificates: "",
  oidcIssuer: connection?.oidcIssuer || "",
  oidcClientId: connection?.oidcClientId || "",
  oidcClientSecret: "",
  oidcTrustEmail: connection?.oidcTrustEmail ?? false,
  allowPortalSignIn: connection?.allowPortalSignIn ?? true,
});

// Settings > Enterprise > Company sign-in: shown to everyone with Settings
// access; the API says whether this user may change it (SysRoot users only),
// and the tab tells the page (onPermission) so its Edit button can say so.
// Like the other settings tabs it is read only until the page's Edit is
// pressed (editing); Cancel or a save ends editing (onEditingDone). The
// status card's Test connection and Remove are not edits of the form.
export const CompanySignInTab = ({
  onSaved = () => {},
  editing = false,
  onEditingDone = () => {},
  onPermission = () => {},
}) => {
  const intl = useIntl();
  const { settings, failed, reload } = useSsoSettings();
  const [saving, setSaving] = useState(false);
  const mayEdit = Boolean(settings?.canEdit);
  useEffect(() => {
    if (settings) onPermission(mayEdit);
  }, [settings, mayEdit, onPermission]);
  const [errorId, setErrorId] = useState(null);
  const message = (id, values) => intl.formatMessage({ id }, values);

  if (!settings) {
    return (
      (failed && <Message severity="error" text={message("something_else_wrong")} />) || (
        <ProgressSpinner style={{ width: "40px", height: "40px" }} />
      )
    );
  }

  // The form's fields and switches change only while editing
  const canEdit = mayEdit && editing;
  const {
    connection,
    serviceProvider,
    requireMfa,
    sysRootUsers = [],
    duplicateEmails = [],
    ssoUserCount = 0,
  } = settings;
  const noSysRootPassword = !sysRootUsers.some((user) => user.hasPassword);
  const secretSaved = connection?.protocol === PROTOCOL_OIDC && connection?.hasOidcClientSecret;
  const sysRootOnly = mayEdit ? "" : message("sysroot_only");
  const tooltipOptions = { position: "bottom" };
  const date = (value) => intl.formatDate(value, { dateStyle: "medium" });
  const TextField = canEdit ? TextInput : ReadOnlyText;

  const status = (!connection && "not_set_up") || (connection.isEnabled && "on") || "off";
  const statusTagClass = {
    not_set_up: "sso-tag-neutral",
    off: "edexlms-tag-warning",
    on: "edexlms-tag-success",
  }[status];
  const savedProtocolLabel = PROTOCOLS.find((option) => option.value === connection?.protocol)?.label;

  // The saved SAML connection was set up from a file or by hand rather than from a metadata URL
  const savedSaml = connection?.protocol === PROTOCOL_SAML ? connection : null;
  const savedWithoutMetadataUrl =
    Boolean(savedSaml) &&
    !savedSaml.samlMetadataUrl &&
    Boolean(savedSaml.samlIdpEntityId || savedSaml.samlSignOnUrl || savedSaml.samlCertificates?.length);
  const certificateNeedsAttention = (savedSaml?.samlCertificates || []).some(
    (certificate) => daysUntil(certificate.expiresAt) <= EXPIRY_WARNING_DAYS
  );
  const advancedChanged =
    Boolean(connection) &&
    (connection.matchBy !== MATCH_BY_EMAIL || Boolean(connection.usernameClaim) || Boolean(connection.oidcTrustEmail));

  const run = async (change, doneId) => {
    setSaving(true);
    setErrorId(null);
    try {
      await change();
      toast.success(message(doneId));
      await reload();
      onSaved();
      return true;
    } catch (error) {
      setErrorId(errorMessageId(error?.response?.data, intl, "something_else_wrong"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async (values) => {
    if (await run(() => SsoService.saveSsoSettings(ssoPayload(values)), "sso_saved")) {
      onEditingDone();
    }
  };

  const submit = (values) => {
    if (connection?.isEnabled && !values.isEnabled) {
      confirmDialog({
        header: message("sso_turn_off_confirm_title"),
        message: message("sso_sso_users_warning", { count: ssoUserCount }),
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptLabel: message("sso_save"),
        rejectLabel: message("cancel"),
        rejectClassName: "p-button-secondary",
        accept: () => save(values),
        reject: () => {},
      });
      return;
    }
    save(values);
  };

  const remove = () => {
    confirmDialog({
      header: message("sso_remove_confirm_title"),
      message: message("sso_sso_users_warning", { count: ssoUserCount }),
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptLabel: message("sso_remove"),
      rejectLabel: message("cancel"),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => run(() => SsoService.removeSso(), "sso_removed"),
      reject: () => {},
    });
  };

  const upload = (event) => {
    const file = event.target.files?.[0];
    // Cleared so the same file can be chosen again after a fix at the provider
    event.target.value = "";
    if (file) {
      run(() => SsoService.uploadSsoMetadata(file), "sso_saved");
    }
  };

  // A tab opened after waiting for the API counts as a pop-up and is
  // blocked, so the tab opens first and is sent on once the link is known.
  const test = async () => {
    setErrorId(null);
    const tab = window.open("", "_blank");
    if (tab) {
      tab.opener = null;
    }
    try {
      const ajax = await SsoService.startSsoTest();
      const startUrl = ajax.data?.response?.startUrl;
      if (tab) {
        tab.location.href = startUrl;
      } else {
        redirectTo(startUrl);
      }
    } catch (error) {
      tab?.close();
      setErrorId(errorMessageId(error?.response?.data, intl, "something_else_wrong"));
    }
  };

  const switchMfa = () =>
    run(() => SsoService.setRequireMfa(!requireMfa), "require_mfa_saved");

  const certificateTag = (expiresAt) => {
    const days = daysUntil(expiresAt);
    if (days < 0) {
      return <Tag className="edexlms-tag-danger" value={message("sso_certificate_is_expired")} />;
    }
    if (days <= EXPIRY_WARNING_DAYS) {
      return <Tag className="edexlms-tag-warning" value={message("sso_certificate_expiring", { days })} />;
    }
    return null;
  };

  return (
    <div className="company-sign-in-tab flex flex-column gap-4">
      <div className="edexlms-section sso-status" data-testid="sso-status-card">
        <div className="sso-status-text">
          <h2 className="sso-status-title">{message("company_sign_in_tab")}</h2>
          <p className="sso-status-help">{message(`sso_status_help_${status}`)}</p>
          <div className="flex flex-wrap align-items-center gap-2 mt-2">
            <Tag rounded className={`sso-status-tag ${statusTagClass}`} data-testid="sso-status">
              {message(`sso_status_${status}`)}
            </Tag>
            {(savedProtocolLabel && (
              <span className="sso-chip" data-testid="sso-status-protocol">
                {message(savedProtocolLabel)}
              </span>
            )) ||
              null}
          </div>
        </div>
        <div className="sso-status-actions flex flex-wrap gap-2">
          {(connection && (
            <TooltipButton
              type="button"
              outlined
              severity="danger"
              icon="pi pi-trash"
              label={message("sso_remove")}
              onClick={remove}
              disabled={!mayEdit || saving}
              tooltip={sysRootOnly}
              tooltipOptions={tooltipOptions}
            />
          )) ||
            null}
          <TooltipButton
            type="button"
            outlined
            icon="pi pi-external-link"
            label={message("sso_test")}
            onClick={test}
            disabled={!mayEdit || !connection || saving}
            tooltip={sysRootOnly || (!connection && message("sso_test_save_first")) || ""}
            tooltipOptions={tooltipOptions}
          />
        </div>
      </div>

      {(!mayEdit && <Message className="justify-content-start" severity="info" text={message("sysroot_only")} />) || null}
      {/* Outside editing the Save bar is hidden: a Test connection or Remove failure shows here */}
      {(!canEdit && errorId && <Message className="justify-content-start" severity="error" text={message(errorId)} />) || null}

      <FinalForm
        key={connection?.updatedAt || "new"}
        initialValues={initialValuesOf(connection)}
        onSubmit={submit}
        render={({ handleSubmit, values, form, dirty, errors, submitFailed }) => {
          const isOidc = Number(values.protocol) === PROTOCOL_OIDC;
          const isSaml = Number(values.protocol) === PROTOCOL_SAML;
          const matchByEmail = Number(values.matchBy) === MATCH_BY_EMAIL;
          const advancedHasError =
            (submitFailed && Boolean(errors?.usernameClaim)) || ADVANCED_ERRORS.includes(errorId);
          return (
            <form onSubmit={handleSubmit} noValidate className="flex flex-column gap-4">
              <div className="sso-layout">
                <div className="sso-main flex flex-column gap-4">
                  <Section title="sso_connect_title">
                    <div className="flex flex-column gap-3">
                      <div className="sso-section-help">{message("sso_connect_help")}</div>
                      <Field
                        name="protocol"
                        component={ProtocolInput}
                        disabled={!canEdit}
                        errorId="sso_invalid_protocol"
                        validate={(value) => (value ? undefined : "required")}
                      />

                      {(isOidc && (
                        <div className="flex flex-column gap-3">
                          <Field
                            name="oidcIssuer"
                            component={TextField}
                            label={message("sso_oidc_issuer")}
                            disabled={!canEdit}
                            required
                            validate={required}
                          />
                          <div className="sso-two-columns">
                            <Field
                              name="oidcClientId"
                              component={TextField}
                              label={message("sso_oidc_client_id")}
                              disabled={!canEdit}
                              required
                              validate={required}
                            />
                            {(canEdit && (
                              <div className="sso-cell">
                                <Field
                                  name="oidcClientSecret"
                                  type="password"
                                  component={TextInput}
                                  label={message("sso_oidc_client_secret")}
                                  disabled={!canEdit}
                                  required={!secretSaved}
                                  validate={secretSaved ? undefined : required}
                                />
                                {(secretSaved && (
                                  <div className="sso-section-help mt-1">{message("sso_oidc_client_secret_saved")}</div>
                                )) ||
                                  null}
                              </div>
                            )) ||
                              null}
                          </div>
                        </div>
                      )) ||
                        null}

                      {(isSaml && (
                        <div className="flex flex-column gap-3">
                          <div>
                            <Field
                              name="samlMetadataUrl"
                              component={TextField}
                              label={message("sso_metadata_url")}
                              disabled={!canEdit}
                            />
                            {(connection?.samlMetadataRefreshedAt && (
                              <div className="sso-section-help mt-1">
                                {message("sso_metadata_refreshed", { date: date(connection.samlMetadataRefreshedAt) })}
                              </div>
                            )) ||
                              null}
                          </div>
                          {(canEdit && (
                            <Disclosure
                              id="sso-metadata-file"
                              label={message("sso_metadata_file_toggle")}
                              defaultOpen={savedWithoutMetadataUrl}
                            >
                              <div className="edexlms-field file-input">
                                <label htmlFor="samlMetadataFile" className="edexlms-field-label block">
                                  {message("sso_metadata_file")}
                                </label>
                                <InputText
                                  id="samlMetadataFile"
                                  type="file"
                                  accept=".xml,text/xml,application/xml"
                                  disabled={saving}
                                  onChange={upload}
                                  className="edexlms-field-input w-full"
                                />
                              </div>
                            </Disclosure>
                          )) ||
                            null}
                          <Disclosure
                            id="sso-manual-details"
                            label={message("sso_manual_toggle")}
                            defaultOpen={savedWithoutMetadataUrl || certificateNeedsAttention}
                            forceOpen={MANUAL_ERRORS.includes(errorId)}
                          >
                            <Field
                              name="samlIdpEntityId"
                              component={TextField}
                              label={message("sso_idp_entity_id")}
                              disabled={!canEdit}
                            />
                            <Field
                              name="samlSignOnUrl"
                              component={TextField}
                              label={message("sso_sign_on_url")}
                              disabled={!canEdit}
                            />
                            {(canEdit && (
                              <div>
                                <Field
                                  name="samlCertificates"
                                  component={TextAreaInput}
                                  label={message("sso_certificates")}
                                  rows={6}
                                />
                                <div className="sso-section-help mt-1">{message("sso_certificates_help")}</div>
                              </div>
                            )) ||
                              null}
                            {(connection?.samlCertificates?.length > 0 && (
                              <ul className="list-none p-0 m-0 flex flex-column gap-2">
                                {connection.samlCertificates.map((certificate) => (
                                  <li key={certificate.thumbprint} className="sso-wrap flex flex-wrap align-items-center gap-2">
                                    <span>
                                      {message("sso_certificate_expires", {
                                        subject: certificate.subject,
                                        date: date(certificate.expiresAt),
                                      })}
                                    </span>
                                    {certificateTag(certificate.expiresAt)}
                                  </li>
                                ))}
                              </ul>
                            )) ||
                              null}
                          </Disclosure>
                        </div>
                      )) ||
                        null}

                      <Disclosure
                        id="sso-advanced"
                        label={message("sso_advanced")}
                        defaultOpen={advancedChanged}
                        forceOpen={advancedHasError}
                      >
                        <Field
                          name="matchBy"
                          component={RadioGroupInput}
                          label={message("sso_match_by")}
                          options={MATCHES}
                          disabled={!canEdit}
                        />
                        {(Number(values.matchBy) === MATCH_BY_USERNAME && (
                          <Field
                            name="usernameClaim"
                            component={TextField}
                            label={message("sso_username_claim")}
                            disabled={!canEdit}
                            required
                            validate={required}
                          />
                        )) ||
                          null}
                        {(isOidc && matchByEmail && (
                          <Field
                            name="oidcTrustEmail"
                            type="checkbox"
                            component={SwitchRowInput}
                            label={message("sso_trust_email")}
                            help={message("sso_trust_email_help")}
                            disabled={!canEdit}
                          />
                        )) ||
                          null}
                      </Disclosure>

                      {(matchByEmail && duplicateEmails.length > 0 && (
                        <Message
                          severity="warn"
                          className="justify-content-start"
                          content={
                            <div className="sso-wrap">
                              <div>{message("sso_duplicate_emails")}</div>
                              <ul className="m-0 mt-2">
                                {duplicateEmails.map((duplicate) => (
                                  <li key={duplicate.email}>
                                    {message("sso_duplicate_count", { email: duplicate.email, count: duplicate.count })}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          }
                        />
                      )) ||
                        null}
                    </div>
                  </Section>

                  <Section title="sso_access_title">
                    <div className="sso-section-help mb-2">{message("sso_access_help")}</div>
                    <div className="sso-switch-rows">
                      <Field
                        name="isEnabled"
                        type="checkbox"
                        component={SwitchRowInput}
                        label={message("sso_enabled")}
                        help={message("sso_enabled_help")}
                        disabled={!canEdit}
                      />
                      {(values.isEnabled && (
                        <Field
                          name="allowPasswordSignIn"
                          type="checkbox"
                          component={SwitchRowInput}
                          label={message("sso_allow_password_sign_in")}
                          help={message(noSysRootPassword ? "sso_password_needed" : "sso_allow_password_sign_in_help")}
                          disabled={!canEdit || (noSysRootPassword && values.allowPasswordSignIn)}
                        />
                      )) ||
                        null}
                      <Field
                        name="allowPortalSignIn"
                        type="checkbox"
                        component={SwitchRowInput}
                        label={message("sso_allow_portal_sign_in")}
                        help={message("sso_allow_portal_sign_in_help")}
                        disabled={!canEdit}
                      />
                    </div>
                  </Section>
                </div>

                <Section title="sso_your_details" className="sso-it-panel">
                  <div className="flex flex-column gap-3">
                    <div className="sso-section-help">{message("sso_your_details_help")}</div>
                    {(!isOidc && (
                      <>
                        <ItValue label={message("sso_entity_id")} value={serviceProvider.entityId} testId="sso-entity-id" />
                        <ItValue label={message("sso_acs_url")} value={serviceProvider.acsUrl} testId="sso-acs-url" />
                        <ItValue label={message("sso_sp_metadata_url")} value={serviceProvider.metadataUrl} testId="sso-sp-metadata-url" />
                      </>
                    )) ||
                      null}
                    {(!isSaml && (
                      <>
                        <ItValue label={message("sso_redirect_uri")} value={serviceProvider.oidcRedirectUri} testId="sso-redirect-uri" />
                        {(values.allowPortalSignIn && (
                          <ItValue
                            label={message("sso_initiate_login_uri")}
                            value={serviceProvider.oidcInitiateLoginUri}
                            testId="sso-initiate-login-uri"
                          />
                        )) ||
                          null}
                      </>
                    )) ||
                      null}
                    <ItValue
                      label={message("sso_start_url")}
                      value={serviceProvider.startUrl}
                      testId="sso-start-url"
                      note={
                        (!values.allowPortalSignIn && (
                          <div className="sso-section-help">{message("sso_start_url_portal_off")}</div>
                        )) ||
                        null
                      }
                    />
                  </div>
                </Section>
              </div>

              {(canEdit && (
                <div className={`sso-action-bar ${dirty ? "is-dirty" : ""}`} data-testid="sso-action-bar">
                  {(errorId && <Message className="w-full justify-content-start" severity="error" text={message(errorId)} />) || null}
                  <div className="sso-action-bar-row">
                    <span className="sso-action-bar-note" aria-live="polite">
                      {(dirty && message("sso_unsaved_changes")) || ""}
                    </span>
                    <div className="sso-action-bar-buttons flex flex-wrap gap-2">
                      <TooltipButton
                        type="button"
                        outlined
                        severity="secondary"
                        icon="pi pi-undo"
                        label={message("cancel")}
                        onClick={() => {
                          form.reset();
                          setErrorId(null);
                          onEditingDone();
                        }}
                        disabled={saving}
                      />
                      <TooltipButton
                        type="submit"
                        icon="pi pi-check"
                        label={message("sso_save")}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              )) ||
                null}
            </form>
          );
        }}
      />

      <div className="edexlms-section">
        <div className="edexlms-section-body">
          <SwitchRow
            inputId="requireMfa"
            label={message("require_mfa")}
            help={message("require_mfa_help")}
            checked={requireMfa}
            onChange={switchMfa}
            disabled={!canEdit || saving}
            tooltip={sysRootOnly}
          />
        </div>
      </div>

      <Section title="sso_emergency_access">
        <div className="sso-section-help mb-3">{message("sso_emergency_access_help")}</div>
        <ul className="list-none p-0 m-0 flex flex-column gap-2">
          {sysRootUsers.map((user) => (
            <li key={user.id} className="sso-wrap flex flex-wrap align-items-center gap-2">
              <span>{user.name}</span>
              <span className="sso-section-help">{user.email}</span>
              {(user.hasPassword && <Tag className="edexlms-tag-success" value={message("sso_can_use_emergency")} />) || (
                <Tag className="edexlms-tag-warning" value={message("sso_no_password")} />
              )}
              {(user.isSso && <Tag className="edexlms-tag-info" value={message("user_type_sso")} />) || null}
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
};

export default CompanySignInTab;
